import { Box, Flex, Skeleton, Stack, Text, useDisclosure } from '@chakra-ui/react';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { amountHeader, DataTableToolbar, DataTableVirtual } from 'src/components/DataTable';
import type { DataTableColumnDef as ColumnDef, DataTableCellContext as CellContext, DataTableRow as Row } from 'src/components/DataTable';
import { FileDownload, FileSelect } from 'src/components/FileSelect';
import { transferService } from 'src/service/TransferService';
import { countries } from 'src/data/Countries';
import Content from 'src/components/Content';
import { MemoLinkCell } from 'src/components/ReachLink';
import { formatElapsedMillis, formatOverview, formatPicker, toTime } from 'src/data/DateTimeUtil';
import { AmountUtil } from 'src/data/AmountUtil';
import {
    Select as ReactSelect
} from "chakra-react-select";
import { LabelAndValueOption } from 'src/data/LabelAndValueOption';
import WaitingSpinner from '@/components/WaitingSpinner';
import YearSelect from '@/components/fields/YearSelect';
import { differenceInMilliseconds } from 'date-fns';
import { Tag } from '@/components/ui/tag';
import { LuTimer } from 'react-icons/lu';
import { IdAndName } from '@/api/IdAndName';
import { CodeAndName } from '@/api/CodeAndName';
import Country from '@/components/Country';
import AddButton from '@/components/buttons/AddButton';
import { useNavigate } from 'react-router-dom';
import { DropDownMenu } from '@/components/DropDownMenu';
import IdAndNameData from 'src/data/IdAndNameData';
import ConfirmDialog from 'src/components/ConfirmDialog';
import FileExchangePanel from '@/components/FileExchangePanel';
import { useI18n } from '@/i18n/I18nContext';

interface RenderTransfer {
    id: number;
    transferDate: IdAndName;
    country: CodeAndName;
    partner: IdAndName;
    project: IdAndName;
    budgetYear: number;
    donationCode: string;
    fundSourceId: (string | null);
    fundSourceName: string;
    renderTotal: string;
    renderOverhead: string;
    renderTransfer: string;
    renderForeignTransfer: string;
    currency: string;
    renderRate: string;
    comments: (string | null);
}

// Memoize the sorting functions to prevent recreating them on each render
// Define these outside the component
const sortByObjectName = (key: 'country' | 'partner' | 'project') => {
    const collator = new Intl.Collator(undefined, { sensitivity: 'base' });
    return (rowA: Row<RenderTransfer>, rowB: Row<RenderTransfer>): number => {
        const valueA = rowA.original[key];
        const valueB = rowB.original[key];
        return collator.compare(valueA.name, valueB.name);
    };
};

const sortByObjectNumber = (key: 'transferDate') => {
    return (rowA: Row<RenderTransfer>, rowB: Row<RenderTransfer>): number => {
        const valueA = rowA.original[key];
        const valueB = rowB.original[key];
        return valueA.id - valueB.id;
    };
};

const OverviewTransfers = () => {
    const { t } = useI18n();
    const [data, setData] = useState<RenderTransfer[]>([]);
    const [deleteData, setDeleteData] = useState<IdAndNameData<number> | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [loadingTime, setLoadingTime] = useState(0);
    // filters
    const [filterYear, setFilterYear] = useState<number | null>(new Date().getFullYear());
    const [filterProjectId, setFilterProjectId] = useState<number | null>(null);
    const [filterProjectOptions, setFilterProjectOptions] = useState<LabelAndValueOption[]>([]);
    const [filterPartnerId, setFilterPartnerId] = useState<number | null>(null);
    const [filterPartnerOptions, setFilterPartnerOptions] = useState<LabelAndValueOption[]>([]);

    const navigate = useNavigate();
    const { open: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();

    const loadAllData = useCallback((): Promise<void> => {
        const startTime = Date.now();
        setIsLoading(true);
        return transferService.query(filterYear, filterProjectId, filterPartnerId)
            .then(r => {
                const partnersMap = new Map(r.partners.map(p => [p.id, p]));
                setFilterPartnerOptions(r.partners.map(p => tuple2OptionItem([p.id, p.name])));
                const projectsMap = new Map(r.projects.map(p => [p.id, p.name]));
                setFilterProjectOptions(r.projects.map(p => tuple2OptionItem([p.id, p.name])));
                const list = r.results.map(t => {
                    const countryCode = partnersMap.get(t.partnerId)?.country;
                    const render: RenderTransfer = {
                        id: t.id,
                        transferDate: { id: toTime(t.transferDate), name: formatOverview(t.transferDate) },
                        country: { code: countryCode, name: countries[countryCode] ?? '' },
                        partner: { id: t.partnerId, name: partnersMap.get(t.partnerId)?.name?.trim() ?? '' },
                        project: { id: t.projectId, name: projectsMap.get(t.projectId)?.trim() ?? '' },
                        budgetYear: t.budgetYear,
                        donationCode: t.donationCode,
                        fundSourceId: t.fundSourceId,
                        fundSourceName: t.fundSourceName,
                        renderTotal: AmountUtil.format(t.total),
                        renderOverhead: `${AmountUtil.format(t.overhead * 100, 1)} %`,
                        renderTransfer: AmountUtil.format(t.transfer),
                        renderForeignTransfer: AmountUtil.format(t.transfer * t.rate),
                        currency: t.currency,
                        renderRate: AmountUtil.format(t.rate),
                        comments: t.comments,
                    };
                    return render;
                })
                setData(list);
            })
            .finally(() => {
                setIsLoading(false);
                const endTime = Date.now();
                const elapsed = differenceInMilliseconds(endTime, startTime);
                setLoadingTime(elapsed);
            });
    }, [filterYear, filterProjectId, filterPartnerId]);

    const onUpload = useCallback((): Promise<void> => {
        console.info(`upload transfers suceeded...`);
        return loadAllData();
    }, [loadAllData]);

    const onDelete = useCallback(async (): Promise<void> => {
        if (!deleteData) {
            return;
        }
        await transferService.remove(deleteData.id);
        setDeleteData(null);
        await loadAllData();
    }, [deleteData]);

    useEffect(() => {
        loadAllData();
    }, [loadAllData]);

    // convenience to update ReactSelect
    const tuple2OptionItem = (on: [number, string]): LabelAndValueOption => {
        return {
            label: on[1].trim(),
            value: on[0]
        }
    }

    const CountryCell = memo(({ value }: { value: CodeAndName }) => (
        <Country code={value.code} name={value.name} />
    ));

    const AmountCell = memo<{ value: string; width: string }>(({ value, width }) => (
        <Text width={width} textAlign='right'>{value}</Text>
    ));

    const columns: ColumnDef<RenderTransfer>[] = useMemo(
        () => [
            {
                id: 'transferDate',
                header: 'Valuta',
                accessorKey: 'transferDate.name',
                sortFn: sortByObjectNumber('transferDate'),
            },
            {
                id: 'country',
                header: 'Land',
                accessorKey: 'country',
                cell: (info: CellContext<RenderTransfer, CodeAndName>) => <CountryCell value={info.getValue()} />,
                sortFn: sortByObjectName('country'),
            },
            {
                id: 'partner',
                header: 'Partner',
                accessorKey: 'partner',
                cell: (info: CellContext<RenderTransfer, IdAndName>) => <MemoLinkCell value={info.getValue()} type="partners" />,
                sortFn: sortByObjectName('partner'),
            },
            {
                id: 'project',
                header: 'Projekt',
                accessorKey: 'project',
                cell: (info: CellContext<RenderTransfer, IdAndName>) => <MemoLinkCell value={info.getValue()} type="projects" />,
                sortFn: sortByObjectName('project'),
            },
            {
                id: 'budgetYear',
                header: 'Fonds Jahr',
                accessorKey: 'budgetYear'
            },
            {
                id: 'donationCode',
                header: 'Kostenstelle',
                accessorKey: 'donationCode'
            },
            {
                id: 'total',
                header: amountHeader({ name: 'Auflösung', width: '6rem', casing: 'none', fontWeight: 'normal', fontSize: '.9rem' }),
                accessorKey: 'renderTotal',
                cell: (info: CellContext<RenderTransfer, string>) => <AmountCell value={info.getValue()} width="6rem" />
            },
            {
                id: 'overhead',
                header: amountHeader({ name: 'Overhead', width: '5rem', casing: 'none', fontWeight: 'normal', fontSize: '.9rem' }),
                accessorKey: 'renderOverhead',
                cell: (info: CellContext<RenderTransfer, string>) => <AmountCell value={info.getValue()} width="6rem" />
            },
            {
                id: 'transfer',
                header: amountHeader({ name: 'Transfer', width: '6rem', casing: 'none', fontWeight: 'normal', fontSize: '.9rem' }),
                accessorKey: 'renderTransfer',
                cell: (info: CellContext<RenderTransfer, string>) => <AmountCell value={info.getValue()} width="6rem" />
            },
            {
                id: 'currency',
                header: 'Währung',
                accessorKey: 'currency'
            },
            {
                id: 'rate',
                header: amountHeader({ name: 'Kurs', width: '5rem', casing: 'none', fontWeight: 'normal', fontSize: '.9rem' }),
                accessorKey: 'renderRate',
                cell: (info: CellContext<RenderTransfer, string>) => <AmountCell value={info.getValue()} width="5rem" />
            },
            {
                id: 'foreignTransfer',
                header: amountHeader({ name: 'Transfer FremdW', width: '8rem', casing: 'none', fontWeight: 'normal', fontSize: '.9rem' }),
                accessorKey: 'renderForeignTransfer',
                cell: (info: CellContext<RenderTransfer, string>) => <AmountCell value={info.getValue()} width="8rem" />
            },
            {
                id: 'comments',
                header: 'Kommentar',
                accessorKey: 'comments'
            },
            {
                id: 'edit',
                enableSorting: false,
                size: 36,
                minSize: 36,
                maxSize: 46,
                cell: (info) => {
                    const row = info.row.original;
                    return <Flex justify='flex-end'>
                        <DropDownMenu
                            onMenuView={() => navigate(`/transfers/view/${row.id}`)}
                            onMenuEdit={() => navigate(`/transfers/edit/${row.id}`)}
                            onMenuDelete={() => {
                                setDeleteData({
                                    id: row.id,
                                    name: `${row.project.name} / ${row.partner.name}`
                                });
                                onDeleteOpen();
                            }}
                        />
                    </Flex>;
                }
            },
        ], [navigate, onDeleteOpen]
    );

    return (
        <Content
            title={t('overviews.transfers.title')}
            description={t('overviews.transfers.description')}
            actions={<>
                <FileExchangePanel>
                    <FileSelect path='/api/funds/transfers/upload' onUpload={onUpload} />
                    <FileDownload scope='all' path='/api/funds/transfers/download' name={`transfers-${formatPicker(new Date())}.xlsx`} />
                </FileExchangePanel>
                <AddButton caption={t('overviews.transfers.create')} onClick={() => navigate('/transfers/add')} />
            </>}
        >
            <Stack gap='6'>
                <DataTableToolbar resultCount={isLoading ? undefined : data.length}
                    filtersActive={filterYear != null || filterProjectId != null || filterPartnerId != null}
                    onClearFilters={() => { setFilterYear(null); setFilterProjectId(null); setFilterPartnerId(null); }}>
                    <Box w={{ base: 'full', md: '10rem' }}>
                        <YearSelect
                            aria-label={t('overviews.filters.year')}
                            isClearable={true}
                            value={filterYear}
                            onChange={(year: number | null) => setFilterYear(year)}
                        />
                    </Box>
                    <Box w={{ base: 'full', md: '20rem' }}>
                        <ReactSelect
                            isSearchable
                            isClearable={true}
                            isDisabled={false}
                            autoFocus={false}
                            name='project-select'
                            aria-label={t('overviews.filters.project')}
                            placeholder={t('overviews.filters.project')}
                            options={filterProjectOptions}
                            onChange={val => setFilterProjectId(val?.value ?? null)}
                            value={filterProjectOptions.find(on => on.value === filterProjectId) ?? null}
                        />
                    </Box>
                    <Box w={{ base: 'full', md: '20rem' }}>
                        <ReactSelect
                            isSearchable
                            isClearable={true}
                            isDisabled={false}
                            autoFocus={false}
                            name='partner-select'
                            aria-label={t('overviews.filters.partner')}
                            placeholder={t('overviews.filters.partner')}
                            options={filterPartnerOptions}
                            onChange={val => setFilterPartnerId(val?.value ?? null)}
                            value={filterPartnerOptions.find(on => on.value === filterPartnerId) ?? null}
                        />
                    </Box>
                </DataTableToolbar>
                {
                    isLoading ? (
                        <Stack py={5}>
                            <WaitingSpinner size='xl' />
                            <Skeleton height='20px' />
                            <Skeleton height='20px' />
                            <Skeleton height='20px' />
                        </Stack>
                    ) : (
                        <>
                            {data.length > 20 && <Text fontSize='sm' color='fg.muted'>{t('common.table.scrollForMore')}</Text>}
                            <DataTableVirtual columns={columns} data={data}
                                initialSortByColumn='transferDate' initialSortByDesc={true}
                                onRowClick={row => navigate(`/transfers/edit/${row.id}`)}
                                hideColumnsOnXs={['fundSourceId', 'currency', 'rate', 'comments', 'edit']} />
                            <Box display='flex' justifyContent='flex-end' alignItems='center'>
                                <Tag variant='outline' startElement={<LuTimer />}>{formatElapsedMillis(loadingTime)}</Tag>
                            </Box>
                            <ConfirmDialog
                                heading={`${deleteData?.name} Löschen`}
                                isOpen={isDeleteOpen}
                                onClose={onDeleteClose}
                                onConfirm={onDelete}
                            />
                        </>
                    )
                }
            </Stack>
        </Content>
    );
}

export default OverviewTransfers
